import React, { useState } from 'react';
import { createEvent, updateEvent } from '../../services/api';
import toast from 'react-hot-toast';

const EventForm = ({ editingItem, categories = [], onClose, onRefresh }) => {
  const [formData, setFormData] = useState({
    title: editingItem?.title || '',
    type: editingItem?.type || '',
    description: editingItem?.description || '',
    startDate: editingItem?.startDate ? editingItem.startDate.split('T')[0] : '',
    startTime: editingItem?.startTime || '10:00',
    endDate: editingItem?.endDate ? editingItem.endDate.split('T')[0] : '',
    endTime: editingItem?.endTime || '17:00',
    location: editingItem?.location || 'Online',
    isVirtual: editingItem?.isVirtual || false,
    price: editingItem?.price || 'Free',
    seats: editingItem?.seats || 0,
    agenda: editingItem?.agenda ? editingItem.agenda.join('\n') : '',
    speakers: editingItem?.speakers ? editingItem.speakers.join('\n') : '',
    imageUrl: editingItem?.imageUrl || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(editingItem?.imageUrl || '');
  const [loading, setLoading] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) return formData.imageUrl;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', imageFile);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/upload/s3/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Image uploaded successfully');
        return result.data.url;
      }
      return formData.imageUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
      return formData.imageUrl;
    } finally {
      setUploading(false);
    }
  };

  const textToArray = (text) => {
    if (!text) return [];
    return text.split('\n').filter(line => line.trim());
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'event',
          order: categories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Category added successfully');
        // In a real app, you would refresh categories from parent
        setFormData({ ...formData, type: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate category is selected
    if (!formData.type) {
      toast.error('Please select an event type');
      return;
    }
    
    setLoading(true);
    
    let imageUrl = formData.imageUrl;
    if (imageFile) {
      imageUrl = await uploadImageToS3();
    }
    
    const submitData = {
      title: formData.title,
      type: formData.type,
      description: formData.description,
      startDate: formData.startDate,
      startTime: formData.startTime,
      endDate: formData.endDate || undefined,
      endTime: formData.endTime || undefined,
      location: formData.location,
      isVirtual: formData.isVirtual,
      price: formData.price,
      seats: parseInt(formData.seats) || 0,
      agenda: textToArray(formData.agenda),
      speakers: textToArray(formData.speakers),
      imageUrl: imageUrl,
      order: parseInt(formData.order) || 0,
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateEvent(editingItem._id, submitData);
        toast.success('Event updated successfully');
      } else {
        await createEvent(submitData);
        toast.success('Event created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.message || 'Error saving event');
    } finally {
      setLoading(false);
    }
  };

  // Get event type options from categories or use defaults
  const eventTypeOptions = categories.length > 0 
    ? categories.filter(cat => cat.isActive !== false).map(cat => ({ 
        value: cat.name, 
        label: cat.name, 
        icon: cat.icon || getCategoryIcon(cat.name)
      }))
    : [
        { value: 'Conference', label: 'Conference', icon: '🎤' },
        { value: 'Webinar', label: 'Webinar', icon: '💻' },
        { value: 'Workshop', label: 'Workshop', icon: '🔧' },
        { value: 'Bootcamp', label: 'Bootcamp', icon: '🏕️' },
        { value: 'Seminar', label: 'Seminar', icon: '📚' },
        { value: 'Networking', label: 'Networking', icon: '🤝' }
      ];

  const getCategoryIcon = (categoryName) => {
    const icons = {
      'Conference': '🎤',
      'Webinar': '💻',
      'Workshop': '🔧',
      'Bootcamp': '🏕️',
      'Seminar': '📚',
      'Networking': '🤝'
    };
    return icons[categoryName] || '📅';
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }} onClick={onClose}>
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        padding: '2rem', 
        maxWidth: '700px', 
        width: '90%', 
        maxHeight: '85vh', 
        overflow: 'auto' 
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Event' : 'Add New Event'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Event Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Event Title *</label>
            <input 
              type="text" 
              required 
              value={formData.title} 
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
              placeholder="Enter event title"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Event Type Selection - First select then see options */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Event Type *</label>
            <select 
              value={formData.type} 
              onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
              required
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
            >
              <option value="">-- Select an event type --</option>
              {eventTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
            
            {!showNewCategoryInput ? (
              <button 
                type="button"
                onClick={() => setShowNewCategoryInput(true)}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: 'transparent',
                  color: '#2C7A7B',
                  border: '1px solid #2C7A7B',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <i className="fas fa-plus"></i> Add New Event Type
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new event type"
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddNewCategory}
                  style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategory('');
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {/* Location */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Location</label>
            <input 
              type="text" 
              value={formData.location} 
              onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
              placeholder="Venue or Online"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Virtual Event Checkbox */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={formData.isVirtual} 
                onChange={(e) => setFormData({ ...formData, isVirtual: e.target.checked })} 
              />
              <span>This is a virtual event (online only)</span>
            </label>
          </div>
          
          {/* Start Date & Time */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Start Date & Time *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <input 
                type="date" 
                required
                value={formData.startDate} 
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} 
                style={{ padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <input 
                type="time" 
                required
                value={formData.startTime} 
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} 
                style={{ padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* End Date & Time */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>End Date & Time</label>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <input 
                type="date" 
                value={formData.endDate} 
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} 
                style={{ padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <input 
                type="time" 
                value={formData.endTime} 
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} 
                style={{ padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* Price and Seats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Price</label>
              <input 
                type="text" 
                value={formData.price} 
                onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                placeholder="Free or $99"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Seats Available</label>
              <input 
                type="number" 
                value={formData.seats} 
                onChange={(e) => setFormData({ ...formData, seats: e.target.value })} 
                placeholder="0 for unlimited"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              rows="3" 
              placeholder="Event description"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Agenda */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Agenda (one per line)</label>
            <textarea 
              value={formData.agenda} 
              onChange={(e) => setFormData({ ...formData, agenda: e.target.value })} 
              rows="4" 
              placeholder="9:00 AM - Registration&#10;10:00 AM - Keynote Speech&#10;11:00 AM - Workshop Session"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Speakers */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Speakers (one per line)</label>
            <textarea 
              value={formData.speakers} 
              onChange={(e) => setFormData({ ...formData, speakers: e.target.value })} 
              rows="3" 
              placeholder="Dr. John Smith - CEO&#10;Jane Doe - Technical Director"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Order and Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
              <input 
                type="number" 
                value={formData.order} 
                onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          
          {/* Event Image */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Event Image</label>
            {imagePreview && (
              <div style={{ marginBottom: '0.5rem' }}>
                <img 
                  src={getFullImageUrl(imagePreview)} 
                  alt="Preview" 
                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E2E8F0' }} 
                />
              </div>
            )}
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#2C7A7B',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}>
                <i className="fas fa-upload"></i> Choose Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange} 
                  style={{ display: 'none' }} 
                />
              </label>
              <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>
                Max 5MB, JPG, PNG, GIF, WEBP
              </small>
            </div>
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => {
                setFormData({ ...formData, imageUrl: e.target.value });
                setImagePreview(e.target.value);
                setImageFile(null);
              }} 
              placeholder="Or enter image URL directly"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#2C7A7B', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600,
                opacity: (loading || uploading) ? 0.7 : 1
              }}
            >
              {loading || uploading ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Event</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#5A6E7F', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600 
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;